import React, { useMemo, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import { Alert, Box, Grid } from '@mui/material'
import keyBy from 'lodash/keyBy'
import type { ImageContentRelationship } from '@/__generated__/gql/graphql'

import { GET_CONTENT_IMAGES } from './queries'
import ContentImagesUploadField from './content-images-upload-field'
import { getContentImagesTypes } from './utils'

interface ContentImagesSectionProps {
  contentId: number
  contentType: string
  language: string
  primaryLanguage: string
}

/**
 * Content Images Section Component
 * Displays and allows uploading and deleting of content images
 *
 * This component uses Apollo Client for data fetching
 */
export default function ContentImagesSection ({
  contentId,
  contentType,
  language,
  primaryLanguage,
}: ContentImagesSectionProps) {
  // Fetch content images via GraphQL
  const { loading, error, data, refetch } = useQuery(GET_CONTENT_IMAGES, {
    variables: {
      contentId,
      contentType,
      language: [language, '_primary'],
    },
    skip: !contentId || !contentType,
  })

  // Get the content images types for the content type
  const contentImagesTypes = useMemo(() => getContentImagesTypes(contentType), [contentType])

  // Extract images from GraphQL response
  const imageContentRelationshipsByType = useMemo<Record<string, ImageContentRelationship>>(() => {
    const content = data?.contentList?.content?.[0]
    return keyBy(content?.imageContentRelationships || [], 'type')
  }, [data])

  // Handle image created/deleted callback - refetch data
  const handleImageChanged = useCallback(() => {
    refetch()
  }, [refetch])

  // Group images into rows of 2
  const imageTypesRows = useMemo(() => {
    const rows = []
    for (let i = 0; i < contentImagesTypes.length; i += 2) {
      rows.push(contentImagesTypes.slice(i, i + 2))
    }
    return rows
  }, [contentImagesTypes])

  if (loading) {
    return (
      <Box>
        <Alert severity="info">Loading images...</Alert>
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">Error loading images: {error.message}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      {imageTypesRows.map((row, rowIndex) => (
        <Grid container spacing={4} key={`content-images-row-${rowIndex}`}>
          {row.map((imageType) => (
            <ContentImagesUploadField
              key={`content-images-${imageType.type}`}
              type={imageType.type}
              title={imageType.title}
              contentId={contentId}
              contentType={contentType}
              language={language}
              primaryLanguage={primaryLanguage}
              image={imageContentRelationshipsByType[imageType.type]?.image}
              onImageCreated={handleImageChanged}
              onImageDeleted={handleImageChanged}
            />
          ))}
        </Grid>
      ))}
    </Box>
  )
}
